import {
    startExistingProductConfiguration,
    startNewProductConfiguration,
    StartProductConfigurationApiV1Parameter,
} from "@husky-hrc/Framework/Services/ProductConfigServiceV1";
import mapApiResult from "@husky-hrc/Framework/Handlers/Data/ProductConfig/MapApiResult";
import { ProductConfigDto, ProductConfigModel, ResponseModel } from "@husky-hrc/Framework/Types/ApiModel";
import Constants from "@husky-hrc/Utilities/Constants";
import isApiError from "@insite/client-framework/Common/isApiError";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import throwErrorIfTesting from "@insite/client-framework/Common/ThrowErrorIfTesting";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnError,
    HasOnException,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = StartProductConfigurationApiV1Parameter &
    HasOnSuccess<ProductConfigModel> &
    HasOnException<ResponseModel> &
    HasOnError;
type Props = {
    apiParameter: StartProductConfigurationApiV1Parameter;
    apiResult: ProductConfigDto;
    parsedResult: ProductConfigModel;
    product?: ProductModel;
    productInfo?: ProductInfo;
    exception?: boolean;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginStartProductConfig: HandlerType = props => {
    throwErrorIfTesting();

    props.dispatch({
        type: Constants.Actions.BeginLoadProductConfig,
        id: props.parameter.productId,
    } as any);
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        // We should never hit this scenario where the product isn't loaded, but just in case.
        props.product = getProductState(props.getState(), props.apiParameter.productId).value;
        if (!props.product || !props.product.detail) {
            props.product = await executeAwaitableHandlerChain(
                loadProduct,
                { id: props.apiParameter.productId },
                props,
            );
        }
        if (props.parameter.configId.trim().length <= 0) {
            props.apiResult = await startNewProductConfiguration(props.apiParameter);
        } else {
            props.apiResult = await startExistingProductConfiguration(props.apiParameter);
        }
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            try {
                props.apiResult = JSON.parse(error.errorMessage);
                props.exception = true;
            } catch {
                props.parameter.onError?.();
                return false;
            }
        } else {
            props.parameter.onError?.();
            return false;
        }
    }
};

export const MapApiResultToModel: HandlerType = async props => {
    props.parsedResult = await executeAwaitableHandlerChain(mapApiResult, { productConfigDto: props.apiResult }, props);
};

export const SetProductInfo: HandlerType = props => {
    const product = props.product;
    if (!product) {
        return;
    }
    props.productInfo = createFromProduct(product);
};

export const DispatchCompleteStartProductConfig: HandlerType = props => {
    props.dispatch({
        type: Constants.Actions.CompleteLoadProductConfig,
        model: props.parsedResult,
        id: props.parameter.productId,
        productInfo: props.productInfo,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.exception) {
        props.parameter.onSuccess?.(props.parsedResult);
    }
};

export const ExecuteOnExceptionCallback: HandlerType = props => {
    if (props.exception) {
        props.parameter.onException?.(props.parsedResult.response);
    }
};

export const chain = [
    DispatchBeginStartProductConfig,
    PopulateApiParameter,
    RequestDataFromApi,
    MapApiResultToModel,
    SetProductInfo,
    DispatchCompleteStartProductConfig,
    ExecuteOnSuccessCallback,
    ExecuteOnExceptionCallback,
];

const startProductConfig = createHandlerChainRunner(chain, "StartProductConfig");
export default startProductConfig;
